import { useEffect, useRef, useState } from "react";
import { useAccount } from "wagmi";
import { api } from "../../lib/api";
import { fetchAllOwnerParcels } from "../../lib/parcels-by-owner";
import { normalizeOwnerIdentity } from "../../domain/territory";
import {
  resolveOwnerTerritories,
  type OwnerTerritory,
} from "../../domain/owner-territories";

/**
 * WO#3 — authenticated owner-territory resolver for the Dashboard.
 *
 * Authentication boundary (Claudio §3): a connected wallet is NOT sufficient.
 * Owner parcels are fetched ONLY when (1) a wallet is connected, (2) a valid
 * existing SIWE/session (`GET /api/v1/auth/me`) exists, and (3) the session
 * wallet identity matches the connected wallet under the canonical identity
 * rule `normalizeOwnerIdentity`. Otherwise: auth_required, no fetch.
 *
 * ONE complete owner fetch → WO#2 adapter → WO#1 buildTerritories. Fail-closed:
 * an incomplete/unavailable owner dataset yields `unavailable`, never partial.
 *
 * Race safety: monotonic request id + AbortController; a slower previous-wallet
 * request can never repopulate the Dashboard, and disconnect clears state.
 */
export type OwnerTerritoriesState =
  | { status: "auth_required" }
  | { status: "loading" }
  | { status: "empty" }
  | { status: "unavailable" }
  | { status: "resolved"; territories: OwnerTerritory[] };

export function useOwnerTerritories(): OwnerTerritoriesState {
  const { address, isConnected } = useAccount();
  const [state, setState] = useState<OwnerTerritoriesState>({
    status: "auth_required",
  });
  const requestIdRef = useRef(0);

  useEffect(() => {
    const myRequestId = ++requestIdRef.current;
    const controller = new AbortController();

    if (!isConnected || !address) {
      setState({ status: "auth_required" });
      return () => controller.abort();
    }

    const connectedId = normalizeOwnerIdentity(address);

    (async () => {
      // 1. Prove an authenticated SIWE session exists and matches the wallet.
      let sessionWallet: string;
      try {
        const me = await api.me();
        sessionWallet = normalizeOwnerIdentity(me.wallet);
      } catch {
        if (myRequestId !== requestIdRef.current) return;
        setState({ status: "auth_required" });
        return;
      }
      if (myRequestId !== requestIdRef.current) return;
      if (sessionWallet !== connectedId) {
        // Authenticated identity differs from the connected wallet → no fetch.
        setState({ status: "auth_required" });
        return;
      }

      // 2. ONE complete authoritative owner fetch (fail-closed).
      setState({ status: "loading" });
      try {
        const parcels = await fetchAllOwnerParcels(address, controller.signal);
        if (myRequestId !== requestIdRef.current) return;
        const territories = resolveOwnerTerritories(parcels, address);
        setState(
          territories.length === 0
            ? { status: "empty" }
            : { status: "resolved", territories },
        );
      } catch {
        if (controller.signal.aborted || myRequestId !== requestIdRef.current)
          return;
        setState({ status: "unavailable" });
      }
    })();

    return () => controller.abort();
  }, [address, isConnected]);

  return state;
}
